/**
 * Batch Edit Utilities — ported from test-beta-6.py JavaScript.
 *
 * Provides structural similarity calculation, token signature extraction,
 * and context-anchored batch editing for similar tokens.
 */

export interface TokenStructure {
  fullLower: string
  prefix: string
  suffix: string
  digitCount: number
  letterCount: number
  hasDash: boolean
  hasDot: boolean
  hasColon: boolean
  hasSlash: boolean
  totalLength: number
  fieldType: string | null
}

export interface TokenSignature {
  token: string
  segmentTypes: string
  segmentCount: number
  digitCount: number
  letterCount: number
  hasDash: boolean
  hasDot: boolean
  hasColon: boolean
  hasSlash: boolean
  category: string
  totalLength: number
}

export interface EditOp {
  op: 'keep' | 'sub' | 'del' | 'ins'
  ch?: string
  old?: string
}

export interface AnchoredEdit {
  type: string
  before: string
  old: string
  new: string
  after: string
  sourcePos: number
  edits: EditOp[]
}

export interface AnchoredResult {
  result: string
  applied: number
  total: number
}

const signatureCache = new Map<string, TokenSignature>()

export function getTokenStructure(token: string, fieldType?: string | null): TokenStructure {
  return {
    fullLower: token.toLowerCase(),
    prefix: token.substring(0, Math.min(3, token.length)),
    suffix: token.substring(Math.max(0, token.length - 3)),
    digitCount: (token.match(/\d/g) || []).length,
    letterCount: (token.match(/[A-Za-z]/g) || []).length,
    hasDash: token.includes('-'),
    hasDot: token.includes('.'),
    hasColon: token.includes(':'),
    hasSlash: token.includes('/'),
    totalLength: token.length,
    fieldType: fieldType || null,
  }
}

export function calculateStructuralSimilarity(tokenA: string, tokenB: string, fieldTypeA?: string | null, fieldTypeB?: string | null): number {
  const structA = getTokenStructure(tokenA, fieldTypeA)
  const structB = getTokenStructure(tokenB, fieldTypeB)
  if (structA.fieldType && structA.fieldType === structB.fieldType) return 1.0
  let score = 0
  if (structA.prefix === structB.prefix && structA.prefix.length >= 2) score += 0.3
  if (structA.suffix === structB.suffix && structA.suffix.length >= 2) score += 0.2
  if (structA.digitCount === structB.digitCount && structA.digitCount > 0) score += 0.15
  const specialMatch = structA.hasDash === structB.hasDash && structA.hasDot === structB.hasDot && structA.hasColon === structB.hasColon && structA.hasSlash === structB.hasSlash
  if (specialMatch && (structA.hasDash || structA.hasDot || structA.hasColon || structA.hasSlash)) score += 0.15
  const lenDiff = Math.abs(structA.totalLength - structB.totalLength)
  if (lenDiff <= 2) score += 0.1
  if (structA.letterCount === structB.letterCount && structA.letterCount > 0) score += 0.1
  return score
}

export function detectSemanticCategory(token: string): string {
  if (/\d{2}:\d{2}/.test(token)) return 'time'
  if (/^\d{2,}[A-Z]?$/.test(token)) return 'room'
  if (/^\d{1,2}-\d{2,}[A-Z]$/i.test(token)) return 'class'
  if (/^[A-Z]{2,4}$/.test(token)) return 'teacher'
  if (/^[A-Z]{3,6}$/.test(token)) return 'subject'
  return 'unknown'
}

export function extractTokenSignature(token: string): TokenSignature {
  if (signatureCache.has(token)) return signatureCache.get(token)!
  const struct = getTokenStructure(token)
  const segments = token.split(/[-.\/:_#+]+/).filter(s => s)
  const segmentTypes = segments.map(s => {
    if (/^\d+$/.test(s)) return 'D'
    if (/^[A-Z]+$/.test(s)) return 'U'
    if (/^[a-z]+$/.test(s)) return 'l'
    if (/^[A-Z]+\d+$/.test(s)) return 'UD'
    if (/^\d+[A-Z]+$/.test(s)) return 'DU'
    if (/^[a-z]+\d+$/.test(s)) return 'lD'
    if (/^\d+[a-z]+$/.test(s)) return 'Dl'
    return 'M'
  }).join('-')
  const sig: TokenSignature = {
    token,
    segmentTypes,
    segmentCount: segments.length,
    digitCount: struct.digitCount,
    letterCount: struct.letterCount,
    hasDash: struct.hasDash,
    hasDot: struct.hasDot,
    hasColon: struct.hasColon,
    hasSlash: struct.hasSlash,
    category: detectSemanticCategory(token),
    totalLength: struct.totalLength,
  }
  signatureCache.set(token, sig)
  return sig
}

export function calculateAdaptiveSimilarity(srcSig: TokenSignature, targetToken: string): number {
  const tgtSig = extractTokenSignature(targetToken)
  let score = 0
  if (srcSig.category !== 'unknown' && srcSig.category === tgtSig.category) score += 0.35
  if (srcSig.segmentTypes === tgtSig.segmentTypes) score += 0.25
  if (srcSig.digitCount > 0 && srcSig.digitCount === tgtSig.digitCount) score += 0.15
  if (srcSig.hasDash === tgtSig.hasDash && srcSig.hasDot === tgtSig.hasDot && srcSig.hasColon === tgtSig.hasColon && srcSig.hasSlash === tgtSig.hasSlash) score += 0.15
  if (srcSig.segmentCount === tgtSig.segmentCount) score += 0.1
  return score
}

export function computeEditOps(oldToken: string, newToken: string): EditOp[] {
  const m = oldToken.length
  const n = newToken.length
  const dp: number[][] = []
  // Levenshtein distance DP table
  // Non-null assertions (!) are safe: dp[i] is initialized before access,
  // and loop bounds guarantee valid indices.
  for (let i = 0; i <= m; i++) {
    dp[i] = []
    dp[i]![0] = i
  }
  for (let j = 0; j <= n; j++) dp[0]![j] = j
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldToken[i - 1] === newToken[j - 1]) {
        dp[i]![j] = dp[i - 1]![j - 1]!
      } else {
        dp[i]![j] = 1 + Math.min(dp[i - 1]![j]!, dp[i]![j - 1]!, dp[i - 1]![j - 1]!)
      }
    }
  }
  const ops: EditOp[] = []
  let i = m
  let j = n
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldToken[i - 1] === newToken[j - 1]) {
      ops.unshift({ op: 'keep', ch: oldToken[i - 1] })
      i--
      j--
    } else if (i > 0 && j > 0 && dp[i]![j] === dp[i - 1]![j - 1]! + 1) {
      ops.unshift({ op: 'sub', old: oldToken[i - 1], ch: newToken[j - 1] })
      i--
      j--
    } else if (i > 0 && dp[i]![j] === dp[i - 1]![j]! + 1) {
      ops.unshift({ op: 'del', old: oldToken[i - 1] })
      i--
    } else {
      ops.unshift({ op: 'ins', ch: newToken[j - 1] })
      j--
    }
  }
  return ops
}

export function applyEditOps(token: string, ops: EditOp[]): string {
  let result = ''
  let tp = 0
  for (const op of ops) {
    if (op.op === 'keep') {
      result += tp < token.length ? token[tp] : ''
      tp++
    } else if (op.op === 'sub') {
      if (tp < token.length && token[tp] === op.old) {
        result += op.ch || ''
      } else if (tp < token.length) {
        result += token[tp]
      }
      tp++
    } else if (op.op === 'del') {
      if (tp < token.length && token[tp] === op.old) {
        // skip
      } else if (tp < token.length) {
        result += token[tp]
      }
      tp++
    } else if (op.op === 'ins') {
      result += op.ch || ''
    }
  }
  while (tp < token.length) {
    result += token[tp]
    tp++
  }
  return result
}

export function computeAnchoredEdits(oldToken: string, newToken: string, contextLen: number): AnchoredEdit[] {
  const ops = computeEditOps(oldToken, newToken)
  const anchored: AnchoredEdit[] = []
  const editGroup: (EditOp & { pos: number })[] = []
  let groupStart = -1
  let pos = 0
  for (const op of ops) {
    if (op.op === 'keep') {
      if (editGroup.length > 0) {
        anchored.push(flushEditGroup(oldToken, editGroup, groupStart, contextLen))
        editGroup.length = 0
        groupStart = -1
      }
      pos++
    } else {
      if (groupStart < 0) groupStart = pos
      editGroup.push({ ...op, pos })
      if (op.op !== 'ins') pos++
    }
  }
  if (editGroup.length > 0) {
    anchored.push(flushEditGroup(oldToken, editGroup, groupStart, contextLen))
  }
  return anchored
}

function flushEditGroup(oldToken: string, editGroup: (EditOp & { pos: number })[], groupStart: number, contextLen: number): AnchoredEdit {
  let oldText = ''
  let newText = ''
  let lastOldPos = groupStart
  for (const e of editGroup) {
    while (lastOldPos < e.pos) {
      oldText += oldToken[lastOldPos]
      lastOldPos++
    }
    if (e.op === 'sub') {
      oldText += e.old || ''
      newText += e.ch || ''
      lastOldPos++
    } else if (e.op === 'del') {
      oldText += e.old || ''
      lastOldPos++
    } else if (e.op === 'ins') {
      newText += e.ch || ''
    }
  }
  const beforeStart = Math.max(0, groupStart - contextLen)
  const afterEnd = Math.min(oldToken.length, lastOldPos + contextLen)
  return {
    type: 'block',
    before: oldToken.substring(beforeStart, groupStart),
    old: oldText,
    new: newText,
    after: oldToken.substring(lastOldPos, afterEnd),
    sourcePos: groupStart,
    edits: editGroup,
  }
}

export function applyAnchoredEdits(token: string, anchored: AnchoredEdit[], minContext: number): AnchoredResult {
  let result = token
  let applied = 0
  const totalEdits = anchored.length
  for (const edit of anchored) {
    let found = false
    const maxCtxBefore = edit.before.length
    const maxCtxAfter = edit.after.length
    let bestCtx = -1
    let bestBefore = -1
    let bestAfter = -1
    let bestIdx = -1
    for (let ctx = maxCtxBefore; ctx >= minContext; ctx--) {
      const beforePart = edit.before.substring(edit.before.length - ctx)
      for (let ctxAfter = maxCtxAfter; ctxAfter >= minContext; ctxAfter--) {
        const afterPart = edit.after.substring(0, ctxAfter)
        const search = beforePart + edit.old + afterPart
        if (search.length === 0) continue
        if (result.includes(search)) {
          const firstIdx = result.indexOf(search)
          const secondIdx = result.indexOf(search, firstIdx + 1)
          if (secondIdx >= 0 && ctx + ctxAfter <= 3) continue
          if (bestCtx < 0 || ctx + ctxAfter > bestCtx + bestAfter) {
            bestCtx = ctx
            bestBefore = ctx
            bestAfter = ctxAfter
            bestIdx = firstIdx
          }
          found = true
          break
        }
      }
      if (found && bestCtx >= 3) break
    }
    if (bestIdx >= 0) {
      const bp = edit.before.substring(edit.before.length - bestBefore)
      const ap = edit.after.substring(0, bestAfter)
      const s = bp + edit.old + ap
      const r = bp + edit.new + ap
      result = result.substring(0, bestIdx) + r + result.substring(bestIdx + s.length)
      applied++
    }
  }
  return { result, applied, total: totalEdits }
}

export function validateEditResult(original: string, result: string, sourceOld: string, sourceNew: string): boolean {
  const sourceDelta = sourceNew.length - sourceOld.length
  const targetDelta = result.length - original.length
  if (sourceDelta !== targetDelta) return false
  if (result.length <= 0) return false
  return true
}

export function applyBatchEdit(otherToken: string, oldToken: string, newToken: string): string {
  const similarity = calculateStructuralSimilarity(oldToken, newToken)
  if (similarity < 0.2) return otherToken

  let anchored = computeAnchoredEdits(oldToken, newToken, 4)
  let result = applyAnchoredEdits(otherToken, anchored, 2)
  if (result.applied === result.total && validateEditResult(otherToken, result.result, oldToken, newToken)) {
    return result.result
  }

  anchored = computeAnchoredEdits(oldToken, newToken, 3)
  result = applyAnchoredEdits(otherToken, anchored, 1)
  if (result.applied === result.total && validateEditResult(otherToken, result.result, oldToken, newToken)) {
    return result.result
  }

  let commonPrefix = 0
  while (commonPrefix < oldToken.length && commonPrefix < newToken.length && oldToken[commonPrefix] === newToken[commonPrefix]) {
    commonPrefix++
  }
  let commonSuffix = 0
  while (commonSuffix < oldToken.length - commonPrefix && commonSuffix < newToken.length - commonPrefix && oldToken[oldToken.length - 1 - commonSuffix] === newToken[newToken.length - 1 - commonSuffix]) {
    commonSuffix++
  }
  const minLen = Math.min(otherToken.length, commonPrefix + commonSuffix)
  if (commonPrefix + commonSuffix > minLen) {
    commonSuffix = Math.max(0, commonSuffix - (commonPrefix + commonSuffix - minLen))
  }
  if (commonPrefix + commonSuffix >= Math.min(oldToken.length, newToken.length) * 0.3) {
    const middle = newToken.substring(commonPrefix, newToken.length - commonSuffix)
    const result2 = otherToken.substring(0, commonPrefix) + middle + otherToken.substring(otherToken.length - commonSuffix)
    if (validateEditResult(otherToken, result2, oldToken, newToken)) {
      return result2
    }
  }

  const ops = computeEditOps(oldToken, newToken)
  const posResult = applyEditOps(otherToken, ops)
  if (validateEditResult(otherToken, posResult, oldToken, newToken)) {
    return posResult
  }

  if (similarity >= 0.5) {
    const lowerOld = oldToken.toLowerCase()
    const lowerOther = otherToken.toLowerCase()
    if (lowerOther.includes(lowerOld)) {
      const idx = lowerOther.indexOf(lowerOld)
      return otherToken.substring(0, idx) + newToken + otherToken.substring(idx + oldToken.length)
    }
    if (lowerOld.includes(lowerOther)) {
      return oldToken.substring(0, otherToken.length) + newToken.substring(otherToken.length)
    }
  }

  return otherToken
}

export function invalidateSignatureCache(token: string): void {
  signatureCache.delete(token)
}

const SEPARATOR_CHARS = new Set(['-', '_', '.', '/', '\\'])

/**
 * Split a confirmed token out of a compound token.
 * Ported from test-beta-6.py split_matched_from_compound.
 *
 * Returns array of remaining parts after extraction, or null if:
 * - confirmed token equals compound token (exact match)
 * - no valid separator boundary found
 * - no remaining content after extraction
 */
export function splitConfirmedFromCompound(confirmedToken: string, compoundToken: string): string[] | null {
  if (confirmedToken.toLowerCase() === compoundToken.toLowerCase()) return null

  const lowerCompound = compoundToken.toLowerCase()
  const lowerConfirmed = confirmedToken.toLowerCase()
  const escaped = confirmedToken.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const splitPattern = new RegExp('[-_./\\\\]*' + escaped + '[-_./\\\\]*', 'i')

  let pos = 0
  while (true) {
    const idx = lowerCompound.indexOf(lowerConfirmed, pos)
    if (idx === -1) break

    const isSep = (ch: string | undefined) => Boolean(ch && (SEPARATOR_CHARS.has(ch) || /\s/.test(ch)))
    const beforeOk = idx === 0 || isSep(compoundToken[idx - 1])
    const endPos = idx + confirmedToken.length
    const afterOk = endPos === compoundToken.length || isSep(compoundToken[endPos])
    const isBoundary = beforeOk && afterOk

    if (isBoundary) {
      const parts = compoundToken.split(splitPattern)
      const remaining = parts.map(p => p.replace(/^[-_./\\ ]+|[-_./\\ ]+$/g, '')).filter(p => p.length > 0)
      return remaining.length > 0 ? remaining : null
    }
    pos = idx + 1
  }

  return null
}
